import Phaser from 'phaser';

import GoldButton from 'ui/button';
import { COLORS } from 'helpers/constants';
import {
  controllerInputForMenuDirections,
  initializeControllerInput
} from 'helpers/controller';
import { drawUIBackground } from './dialogue';

const sceneKey = 'ConfirmationDialogScene';

export const showConfirmationDialog = (scene, options = {}) => {
  return new Promise(resolve => {
    scene.scene.launch(sceneKey, {
      ...options,
      resolve,
      showCancel: options.showCancel !== false
    });
    scene.scene.bringToTop(sceneKey);
  });
};

export const showAlertDialog = (scene, options = {}) => {
  const dialogOptions =
    typeof options === 'string' ? { message: options } : options;
  return showConfirmationDialog(scene, {
    title: dialogOptions.title || 'Notice',
    message: dialogOptions.message || '',
    confirmLabel: dialogOptions.confirmLabel || 'OK',
    showCancel: false
  });
};

export const showGlobalAlertDialog = options => {
  const dialogOptions =
    typeof options === 'string' ? { message: options } : options;
  const activeScene = window.game?.scene
    ?.getScenes(true)
    ?.slice()
    ?.reverse()
    ?.find(scene => scene.scene?.key !== sceneKey);

  if (activeScene) {
    return showAlertDialog(activeScene, dialogOptions);
  }

  window.alert?.(dialogOptions.message || '');
  return Promise.resolve(true);
};

export default class ConfirmationDialogScene extends Phaser.Scene {
  constructor() {
    super(sceneKey);
  }

  init(data) {
    this.data = data;
    this.selectedIndex = data.initialSelection === 'cancel' ? 1 : 0;
    window.__clInputModalScene = sceneKey;
  }

  create() {
    this.drawDialog();
    this.addInputs();
  }

  drawDialog() {
    const scale = this.game.defaultZoom;
    const width = Math.min(window.innerWidth - 48 * scale, 280 * scale);
    const height = 126 * scale;
    const x = Math.floor(window.innerWidth / 2 - width / 2);
    const y = Math.floor(window.innerHeight / 2 - height / 2);

    this.backdrop = this.add.graphics().setDepth(100);
    this.backdrop.fillStyle(0x000000, 0.55);
    this.backdrop.fillRect(0, 0, window.innerWidth, window.innerHeight);
    this.backdrop.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, window.innerWidth, window.innerHeight),
      Phaser.Geom.Rectangle.Contains
    );
    this.backdrop.on('pointerup', (pointer, localX, localY, event) => {
      event?.stopPropagation?.();
    });

    this.panel = drawUIBackground(
      this,
      new Phaser.Geom.Rectangle(x, y, width, height),
      scale
    ).setDepth(101);

    const title = this.data.title || 'Confirm';
    this.titleText = this.add
      .text(window.innerWidth / 2, y + 18 * scale, title, {
        fontSize: `${10 * scale}px`,
        fontFamily: 'pixelMix',
        color: COLORS.LIGHT_YELLOW,
        align: 'center'
      })
      .setOrigin(0.5)
      .setResolution(10)
      .setDepth(102);

    this.messageText = this.add
      .text(x + 16 * scale, y + 42 * scale, this.data.message || '', {
        fontSize: `${8 * scale}px`,
        fontFamily: 'pixelMix',
        color: COLORS.LIGHT_BLUE,
        wordWrap: { width: width - 32 * scale },
        align: 'center'
      })
      .setOrigin(0)
      .setResolution(10)
      .setDepth(102);

    this.buttons = [];
    const buttonY = y + height - 26 * scale;
    const buttonWidth = 88;
    const buttonHeight = 18;
    const confirmButton = GoldButton(this, {
      text: this.data.confirmLabel || 'OK',
      x:
        this.data.showCancel === false
          ? window.innerWidth / 2
          : x + width * 0.34,
      y: buttonY,
      width: buttonWidth,
      height: buttonHeight,
      action: () => this.resolveAndClose(true)
    }).setDepth(102);
    this.buttons.push(confirmButton);

    if (this.data.showCancel !== false) {
      const cancelButton = GoldButton(this, {
        text: this.data.cancelLabel || 'Cancel',
        x: x + width * 0.66,
        y: buttonY,
        width: buttonWidth,
        height: buttonHeight,
        action: () => this.resolveAndClose(false)
      }).setDepth(102);
      this.buttons.push(cancelButton);
    }

    this.updateSelectedButton();
  }

  addInputs() {
    const [
      addControllerInputs,
      removeControllerInputs
    ] = initializeControllerInput(
      this,
      this.moveInDirection,
      () => {},
      this.confirmSelected,
      this.confirmSelected,
      () => {},
      () => {},
      () => {},
      () => {},
      () => {},
      () => {},
      () => {},
      this.cancel
    );

    this.addControllerInputs = addControllerInputs;
    this.removeControllerInputs = removeControllerInputs;
    this.input.gamepad?.on('up', this.handleGamepadButtonUp);
  }

  handleGamepadButtonUp = (pad, button) => {
    if (!button) {
      return;
    }

    if (button.index === 0) {
      this.confirmSelected();
    } else if (button.index === 1) {
      this.cancel();
    }
  };

  update() {
    controllerInputForMenuDirections(this, this.moveInDirection);
  }

  moveInDirection = direction => {
    if (this.buttons.length < 2) {
      return;
    }

    if (direction[0] !== 0 || direction[1] !== 0) {
      this.selectedIndex = this.selectedIndex === 0 ? 1 : 0;
      this.updateSelectedButton();
    }
  };

  updateSelectedButton() {
    this.buttons.forEach((button, index) => {
      button.setSelected(index === this.selectedIndex);
    });
  }

  confirmSelected = () => {
    if (this.buttons[this.selectedIndex]?.action) {
      this.buttons[this.selectedIndex].action();
    }
  };

  cancel = () => {
    if (this.data.showCancel === false) {
      this.resolveAndClose(true);
    } else {
      this.resolveAndClose(false);
    }
  };

  resolveAndClose(value) {
    this.data.resolve?.(value);
    this.close();
  }

  close() {
    this.removeControllerInputs?.();
    this.input.gamepad?.off('up', this.handleGamepadButtonUp);
    if (window.__clInputModalScene === sceneKey) {
      window.__clInputModalScene = null;
    }
    this.scene.stop();
  }
}
